/**
 * Per-user site style ("theme").
 *
 * A user picks one of these in Profile → Configurations; the choice is stored on
 * the user's `configuration.style` (backend) and applied by toggling a
 * `data-theme` attribute (and the `.dark` class) on <html>. An empty choice
 * falls back to the company default (`globalSettings.ui.siteStyle`), then to
 * `modern`. The actual look of each style lives in `src/styles/themes.scss`,
 * driven by CSS variables.
 *
 *  - modern       — the current flat, soft-surfaced look (default).
 *  - classic      — the vibrant, higher-depth look the app had before the redesign.
 *  - studio       — the nexys.com brand look: warm paper canvas, violet ink, Outfit type.
 *  - studio-<hue> — the same Studio surfaces with a different accent (red, orange,
 *                   blue, green, black). Palette only; everything else is inherited.
 *  - studio-dark  — Studio on a violet-black canvas.
 *  - studio-black-dark — a genuinely black-surfaced Studio (the counterpart to
 *                   studio-black, which is a black accent on the paper canvas).
 *  - dark         — dark surfaces.
 *
 * Naming is load-bearing. themes.scss matches styles by SHAPE, not by exact name:
 * `[data-theme^='studio']` carries Studio's type/shape to every studio variant, and
 * `[data-theme$='dark']` carries the dark safety net to every dark variant. A new
 * style called `<x>-dark` inherits that net for free; one called `darkX` would not.
 *
 * Adding a style here also needs it added to `USER_STYLES` in the backend's
 * `entities/user.entity.ts` — `user-configuration.dto.ts` validates the saved
 * value with `@IsIn(USER_STYLES)` and rejects anything else.
 */
export type UserStyle =
  | 'modern'
  | 'classic'
  | 'studio'
  | 'studio-red'
  | 'studio-orange'
  | 'studio-blue'
  | 'studio-green'
  | 'studio-black'
  | 'studio-dark'
  | 'studio-black-dark'
  | 'dark'

export const USER_STYLES: UserStyle[] = [
  'modern',
  'classic',
  'studio',
  'studio-red',
  'studio-orange',
  'studio-blue',
  'studio-green',
  'studio-black',
  'studio-dark',
  'studio-black-dark',
  'dark',
]

export const USER_STYLE_LABELS: Record<UserStyle, string> = {
  modern: 'Modern',
  classic: 'Classic',
  studio: 'Studio',
  'studio-red': 'Studio Red',
  'studio-orange': 'Studio Orange',
  'studio-blue': 'Studio Blue',
  'studio-green': 'Studio Green',
  'studio-black': 'Studio Black',
  'studio-dark': 'Studio Dark',
  'studio-black-dark': 'Studio Black Dark',
  dark: 'Dark',
}

const STORAGE_KEY = 'nexys.userStyle'
const DEFAULT_STYLE: UserStyle = 'modern'

const isUserStyle = (value: any): value is UserStyle => USER_STYLES.includes(value)

/**
 * User choice wins. An empty or unknown user value falls through to the company
 * default, then to modern.
 */
export const resolveTheme = (userStyle?: string | null, companyStyle?: string | null): UserStyle => {
  if (isUserStyle(userStyle)) return userStyle
  if (isUserStyle(companyStyle)) return companyStyle
  return DEFAULT_STYLE
}

/**
 * The Studio styles are the only ones using a webfont the app does not already load
 * (index.html ships Work Sans). Fetch Outfit on demand the first time a Studio
 * style is applied so every other user pays nothing for it. The <link> is left in place
 * afterwards, so toggling between Studio and Studio Dark never re-flashes.
 */
const isStudio = (style: UserStyle) => style.startsWith('studio')
const isDark = (style: UserStyle) => style.endsWith('dark')

const STUDIO_FONT_ID = 'nexys-studio-font'
const STUDIO_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap'

const ensureStudioFont = () => {
  try {
    if (document.getElementById(STUDIO_FONT_ID)) return
    const link = document.createElement('link')
    link.id = STUDIO_FONT_ID
    link.rel = 'stylesheet'
    link.href = STUDIO_FONT_HREF
    document.head.appendChild(link)
  } catch {
    // no document / CSP blocked the stylesheet — the theme falls back to Work Sans.
  }
}

/** The last-applied style, read synchronously at boot to avoid a flash before Redux rehydrates. */
export const getStoredStyle = (): UserStyle => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isUserStyle(stored)) return stored
  } catch {
    // localStorage unavailable (private mode / SSR) — fall through to default.
  }
  return DEFAULT_STYLE
}

/** Apply a style to the document: sets `data-theme`, toggles `.dark`, and remembers the choice. */
export const applyTheme = (style?: UserStyle | string | null): UserStyle => {
  const next: UserStyle = isUserStyle(style) ? style : DEFAULT_STYLE
  const root = document.documentElement
  root.setAttribute('data-theme', next)
  root.classList.toggle('dark', isDark(next))
  if (isStudio(next)) ensureStudioFont()
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // ignore persistence failures
  }
  return next
}
